import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useState } from 'react';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { monthlyChargesMsg, oneTimePaymentMsg, quantityMsg, t } from '../../../../common/i18n';
import type { TableProps } from '@design-system/component-library/src/components/Table/Table';
import type { ReactNode } from 'react';

import './PlaceOrderSummaryView.scss';

const { Button, Table } = CL;

export interface SimpleSummaryData {
  totalHeading: string;
  monthlyLabel: string | ReactNode;
  monthlyValue: string | ReactNode;
  monthlyVat: string | ReactNode;
  showMonthly?: boolean;
  showOneTime?: boolean;
  oneTimeLabel: string | ReactNode;
  oneTimeValue: string | ReactNode;
  oneTimeVat: string | ReactNode;
}

export interface TableRow {
  rowHeader?: boolean;
  indented?: boolean;
  cssClass?: string;
  [key: string]: ReactNode | boolean | undefined;
}

export interface DetailedSummaryData {
  rows: TableRow[];
  footer?: TableRow[];
}

export interface PlaceOrderSummaryViewProps {
  className?: string;
  simpleSummary: SimpleSummaryData;
  detailed?: DetailedSummaryData;
  toggleLabels?: { showDetails: string; hideDetails: string };
  defaultOpen?: boolean;
  collapsible?: boolean;
}

const DefaultToggleLabels = {
  showDetails: t.CUQU('Show details'),
  hideDetails: t.TQNV('Hide details'),
};

const SimpleSummarySection = ({ data }: { data: SimpleSummaryData }) => {
  const {
    totalHeading,
    monthlyLabel,
    monthlyValue,
    monthlyVat,
    showMonthly = true,
    showOneTime = true,
    oneTimeLabel,
    oneTimeValue,
    oneTimeVat,
  } = data;

  const columns: TableProps['columns'] = [
    { key: 'label', label: '', align: 'left' },
    { key: 'value', label: '', align: 'right' },
  ];

  const rows = [
    ...(showOneTime
      ? [
          {
            label: <strong>{oneTimeLabel}</strong>,
            value: (
              <>
                <div className={dsClass.H4}>{oneTimeValue}</div>
                <div>{oneTimeVat}</div>
              </>
            ),
          },
        ]
      : []),
    ...(showMonthly
      ? [
          {
            label: <strong>{monthlyLabel}</strong>,
            value: (
              <>
                <div className={dsClass.H4}>{monthlyValue}</div>
                <div>{monthlyVat}</div>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div>
        <span className={classNames(dsClass.H3, dsClass.MARGIN_LEFT_3)}>{totalHeading}</span>
      </div>
      <Table className="order-summary-simple" columns={columns} rows={rows} tableType="nude" showHeaders={true} />
    </>
  );
};

const MobileChargeSummary = ({ label, value, vat }: { label: ReactNode; value: ReactNode; vat?: ReactNode }) => (
  <div className="of-order-summary-mobile-totals__charge">
    <strong>{label}</strong>
    <div>{value}</div>
    {vat ? (
      <div>
        {t.UZ6X('VAT')} {vat}
      </div>
    ) : null}
  </div>
);

export const MobileDetailedTotals = ({ rows, footer }: DetailedSummaryData) => {
  const total = rows.find(row => row.cssClass === 'total');
  const totalVat = footer?.find(row => row.cssClass === 'total-vat');
  const totalWithTax = footer?.find(row => row.cssClass === 'total-with-tax');

  if (!total || !totalWithTax) {
    return null;
  }

  return (
    <div className="of-order-summary-mobile-totals">
      <div className="of-order-summary-mobile-totals__section">
        <div className="of-order-summary-mobile-totals__heading">{total.name}</div>
        <MobileChargeSummary label={t.GOBY(oneTimePaymentMsg)} value={total.onetime} vat={totalVat?.onetime} />
        <MobileChargeSummary label={t.P6BC(monthlyChargesMsg)} value={total.monthly} vat={totalVat?.monthly} />
      </div>
      <div className="of-order-summary-mobile-totals__section">
        <div className="of-order-summary-mobile-totals__heading">{totalWithTax.name}</div>
        <MobileChargeSummary label={t.GOBY(oneTimePaymentMsg)} value={totalWithTax.onetime} />
        <MobileChargeSummary label={t.P6BC(monthlyChargesMsg)} value={totalWithTax.monthly} />
      </div>
    </div>
  );
};

const DetailedSummarySection = ({ data }: { data: DetailedSummaryData }) => {
  const { rows, footer = [] } = data;

  const clColumns: TableProps['columns'] = [
    {
      key: 'name',
      label: t.C84H('Product'),
      align: 'left',
      width: '50%',
    },
    {
      key: 'amount',
      label: t.M0W7(quantityMsg),
      align: 'left',
      width: '10%',
    },
    {
      key: 'onetime',
      label: t.GOBY(oneTimePaymentMsg),
      align: 'right',
      width: '20%',
    },
    {
      key: 'monthly',
      label: t.P6BC(monthlyChargesMsg),
      align: 'right',
      width: '20%',
    },
  ];

  const footerColumns: TableProps['columns'] = [
    {
      key: 'name',
      label: '',
      align: 'left',
    },
    {
      key: 'onetime',
      label: '',
      align: 'right',
    },
    {
      key: 'monthly',
      label: '',
      align: 'right',
    },
  ];

  const toCells = (row: TableRow): Record<string, ReactNode> => {
    const formatCell = (cellValue: ReactNode, addClassNames?: string[]): ReactNode => {
      const value = cellValue ?? '-';
      const hasEmptyValue = !cellValue;
      const allClassNames = classNames(
        row.cssClass as string,
        addClassNames,
        hasEmptyValue && 'of-order-summary-empty-value'
      );

      return <span className={allClassNames}>{value}</span>;
    };

    return {
      name: (
        <div
          className={classNames(
            row.cssClass as string,
            'name',
            row.rowHeader ? 'of-order-summary-row-bolded' : undefined,
            row.indented ? 'of-order-summary-row-indented' : undefined
          )}
        >
          <span>{row.name}</span>
        </div>
      ),
      amount: formatCell(row.amount, ['amount']),
      onetime: formatCell(row.onetime, ['onetime']),
      monthly: formatCell(row.monthly, ['monthly']),
    };
  };

  const body: TableProps['rows'] = rows.map(toCells);
  const foot: TableProps['rows'] | undefined = footer.length ? footer.map(toCells) : undefined;
  return (
    <>
      <div>
        <Table columns={clColumns} rows={body} tableType="bordered" hideHeadersOnMobile={false} />
      </div>
      <div>
        <Table
          className="order-summary-footer"
          columns={footerColumns}
          rows={foot}
          tableType="nude"
          showHeaders={false}
          hideHeadersOnMobile={false}
        />
      </div>
      <MobileDetailedTotals rows={rows} footer={footer} />
    </>
  );
};

export const PlaceOrderSummaryView = (props: PlaceOrderSummaryViewProps) => {
  const {
    className,
    simpleSummary,
    detailed,
    toggleLabels = DefaultToggleLabels,
    defaultOpen = true,
    collapsible = true,
  } = props;

  const [open, setOpen] = useState(defaultOpen);
  const hasDetails = !!detailed;
  const showToggle = hasDetails && collapsible;
  const showDetails = hasDetails && (!collapsible || open);

  return (
    <div
      className={classNames(
        'of-order-summary-view',
        dsClass.PADDING_VERTICAL_4,
        dsClass.PADDING_HORIZONTAL_3,
        className
      )}
    >
      <SimpleSummarySection data={simpleSummary} />
      {showToggle && (
        <div className={dsClass.TEXT_ALIGN_END}>
          <Button
            size="l"
            className={classNames(dsClass.MARGIN_RIGHT_3)}
            color="link-button"
            onClick={() => setOpen(o => !o)}
          >
            {open ? toggleLabels.hideDetails : toggleLabels.showDetails}
          </Button>
        </div>
      )}
      {showDetails && detailed ? (
        <div>
          <DetailedSummarySection data={detailed} />
        </div>
      ) : null}
    </div>
  );
};
